import React from 'react';
import { Link } from 'react-router-dom';
import logo from './components/logo-adjusted.png';
import './PeopleExplorerPublicPage.css';

const FEATURES = [
  ['Search across subjects', 'Find people directly, or begin with a television series, film, music act, book or historical subject.'],
  ['See the whole cast', 'Open a television work to review cast and crew together, including living, deceased and unknown-status people.'],
  ['Understand every fact', 'Dates, age calculations, sources, confidence and conflicts remain visible instead of being flattened into false certainty.'],
  ['Keep your place', 'Open a person or comparison in a full-screen depth layer, then return to the same results, filters and scroll position.'],
  ['Private by design', 'Search history, favourites and cached public data stay on your device. VISINVOKE does not build a private people profile.'],
  ['Useful, not sensational', 'Cause-of-death information is shown only when a public source supports it, with explicit unknown and conflict states.'],
];

const FAQ = [
  ['Is People Explorer free?', 'Yes. It is a full VISINVOKE utility included free with a VISINVOKE account.'],
  ['Does it guess missing facts?', 'No. Missing and conflicting information is labelled clearly, and cause-of-death information is never inferred.'],
  ['Where does the information come from?', 'The first release uses Wikidata, Wikimedia Commons and TVmaze, with source links and attribution shown in the product.'],
  ['Can I use it on a phone?', 'The People Explorer interface is responsive and touch-safe. Native mobile packaging will follow the wider VISINVOKE platform audit.'],
];

function usePeopleExplorerMetadata() {
  React.useEffect(() => {
    const previousTitle = document.title;
    document.title = 'People Explorer | VISINVOKE';
    let description = document.querySelector('meta[name="description"]');
    const created = !description;
    if (!description) {
      description = document.createElement('meta');
      description.name = 'description';
      document.head.appendChild(description);
    }
    const previousDescription = description.content;
    description.content = 'Explore public facts about people, casts and creative works with visible sources, confidence and respectful handling of uncertainty.';
    return () => {
      document.title = previousTitle;
      if (created) description.remove();
      else description.content = previousDescription;
    };
  }, []);
}

export default function PeopleExplorerPublicPage() {
  usePeopleExplorerMetadata();
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'VISINVOKE People Explorer',
    applicationCategory: 'ReferenceApplication',
    operatingSystem: 'Windows',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'AUD' },
    description: 'A source-aware explorer for public information about people, casts and creative works.',
  };

  return (
    <main className="pe-public">
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      <nav className="pe-public__nav" aria-label="People Explorer navigation">
        <Link to="/login" className="pe-public__brand"><img src={logo} alt="VISINVOKE" /></Link>
        <div className="pe-public__nav-actions">
          <a href="#how-it-works">How it works</a>
          <Link className="pe-public__nav-button" to="/register">Create free account</Link>
        </div>
      </nav>

      <header className="pe-public__hero">
        <div className="pe-public__hero-copy">
          <p className="pe-public__eyebrow">FREE VISINVOKE UTILITY</p>
          <h1>People are more than a search result.</h1>
          <p className="pe-public__lead">Explore the people behind television, film, music, books and history—with the source, confidence and uncertainty kept in view.</p>
          <div className="pe-public__actions">
            <Link className="pe-public__primary" to="/register">Get People Explorer free</Link>
            <Link className="pe-public__secondary" to="/login">Sign in</Link>
          </div>
          <p className="pe-public__note">Full version · No trial · Launches through the VISINVOKE Desktop Command Centre</p>
        </div>
        <div className="pe-public__lens" aria-label="Illustration of a source-aware people search">
          <span className="pe-public__lens-ring"><span>PE</span></span>
          <div className="pe-public__signal pe-public__signal--one">SOURCE</div>
          <div className="pe-public__signal pe-public__signal--two">CONFIDENCE</div>
          <div className="pe-public__signal pe-public__signal--three">CONTEXT</div>
        </div>
      </header>

      <section className="pe-public__trust" aria-label="People Explorer principles">
        <span>Public sources</span><span>Visible attribution</span><span>Local history and favourites</span><span>No AI guessing</span>
      </section>

      <section className="pe-public__section" id="how-it-works">
        <p className="pe-public__eyebrow">A CLEARER WAY TO EXPLORE</p>
        <h2>Built for the questions that lead to other questions</h2>
        <div className="pe-public__features">
          {FEATURES.map(([title, body], index) => (
            <article key={title}><span>{String(index + 1).padStart(2, '0')}</span><h3>{title}</h3><p>{body}</p></article>
          ))}
        </div>
      </section>

      <section className="pe-public__section pe-public__sources">
        <div>
          <p className="pe-public__eyebrow">SOURCES STAY ATTACHED</p>
          <h2>Confidence is part of the answer.</h2>
          <p>People Explorer preserves provider identifiers and shows whether a fact is supported, unverified or conflicting. When sources disagree, you see the disagreement.</p>
        </div>
        <div className="pe-public__source-card">
          <div><strong>Born</strong><span>17 January 1922</span><em>Single source</em></div>
          <div><strong>Age at event</strong><span>42</span><em>Calculated from day-precise dates</em></div>
          <div><strong>Cause of death</strong><span>No reliable public cause found</span><em>Not inferred</em></div>
        </div>
      </section>

      <section className="pe-public__section pe-public__faq">
        <p className="pe-public__eyebrow">COMMON QUESTIONS</p>
        <h2>What to know before you explore</h2>
        <div>{FAQ.map(([question, answer]) => <details key={question}><summary>{question}</summary><p>{answer}</p></details>)}</div>
      </section>

      <section className="pe-public__cta">
        <div><p className="pe-public__eyebrow">VISINVOKE MEANS VALUE</p><h2>Explore with evidence, not guesswork.</h2></div>
        <Link className="pe-public__primary" to="/register">Create your free account</Link>
      </section>

      <footer className="pe-public__footer"><span>© VISINVOKE</span><span>Quality. Ownership. Rewards.</span></footer>
    </main>
  );
}
